/**
 * Gated Vimeo OTT playback.
 *
 * The server emits a placeholder (`[data-ott-video]` with `data-video-id`) instead of a direct
 * iframe. For each placeholder we ask the server to authorize the viewer and return a ready-made,
 * short-lived player iframe. Entitlement is resolved server-side (OTT customer lookup by email), so
 * the browser never sees the API key or the token minting.
 *
 * Live events use `data-ott-live-event-id` instead: the server resolves the event's current video
 * (it changes per occurrence) and returns the player in live mode.
 */

interface OttEmbedResponse {
    entitled: boolean;
    iframe?: string;
}

const ENDPOINT = '/umbraco/api/OttEmbedApi/GetAuthorizedEmbed';
const LIVE_ENDPOINT = '/umbraco/api/OttEmbedApi/GetAuthorizedLiveEmbed';

const getEmbedUrl = (container: HTMLElement): string | null => {
    const liveEventId = container.getAttribute('data-ott-live-event-id');
    if (liveEventId) {
        return `${LIVE_ENDPOINT}?liveEventId=${encodeURIComponent(liveEventId)}`;
    }

    const videoId = container.getAttribute('data-video-id');
    return videoId ? `${ENDPOINT}?videoId=${encodeURIComponent(videoId)}` : null;
};

const loadOttVideo = async (container: HTMLElement): Promise<void> => {
    const embedUrl = getEmbedUrl(container);
    const target = container.querySelector('[data-ott-target]') as HTMLElement | null;
    const notEntitled = container.querySelector('[data-ott-not-entitled]') as HTMLElement | null;
    const fallbackImage = container.querySelector('[data-ott-fallback]') as HTMLElement | null;
    const overlay = container.querySelector('[data-ott-overlay]') as HTMLElement | null;

    if (!embedUrl || !target) {
        return;
    }

    // Scrim keeps the fallback message legible over the fallback image (site-wide overlay pattern).
    const showNotEntitled = () => {
        if (notEntitled) notEntitled.hidden = false;
        if (overlay) overlay.hidden = false;
    };

    try {
        const response = await fetch(embedUrl, {
            headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
            showNotEntitled();
            return;
        }

        const data = (await response.json()) as OttEmbedResponse;

        if (data.entitled && data.iframe) {
            target.innerHTML = data.iframe;
            if (fallbackImage) fallbackImage.remove();
            if (overlay) overlay.remove();
        } else {
            showNotEntitled();
        }
    } catch {
        showNotEntitled();
    }
};

/**
 * Initialize gated OTT video placeholders on the page. Guarded by data-ott-initialized so it is
 * safe to call again after dynamically adding placeholders (mirrors initializeVideoItems).
 */
export const initializeOttVideos = (): void => {
    const containers = Array.prototype.slice.call(
        document.querySelectorAll('[data-ott-video]:not([data-ott-initialized])')
    ) as HTMLElement[];

    containers.forEach(container => {
        container.setAttribute('data-ott-initialized', 'true');
        void loadOttVideo(container);
    });
};
